import { ScenarioExt } from '@/classes/scenario/ScenarioExt';
import { IRule } from '@/classes/rules/IRule';
import { DefaultValue } from '@/classes/rules/DefaultValue';
import { Invalidation } from '@/classes/rules/Invalidation';
import { FieldUsage } from '@/classes/rules/FieldUsage';
import _t from '@/helpers/timeHelper';
import { getValueFromPath } from '@/helpers/pathHelper';

const noDefaultValue = () => null;
const available = () => true;

/**
 * IRule to handle Date objects. Has a min and a max fields that are dates too,
 * the checkValue checks that the Date value is between min and max Dates.
 */
export class DateRule<Context = ScenarioExt> implements IRule<Context> {

  public key: string;
  public min: Date | string | undefined;
  public max: Date | string | undefined;
  public format: string;
  public required: boolean = true;
  public displayed: boolean = true;
  public fieldUsage: FieldUsage = new FieldUsage();
  public isAvailable: (context: Context) => boolean = available;
  public getDefaultValue: (context?: Context) => DefaultValue<Date> | null = noDefaultValue;

  public constructor(
    key: string,
    min?: Date | string | undefined,
    max?: Date | string | undefined,
    format?: string
  ) {
    this.key = key;
    this.min = min;
    this.max = max;
    this.format = format ?? 'YYYY-MM-DD';
  }

  public get invalidation(): Invalidation {
    return this.fieldUsage.getInvalidation();
  }

  public checkValue(val: Date | null, context: Context): boolean | null {
    if (val === null) {
      return null;
    }
    const minimum = this.minimum(context);
    const maximum = this.maximum(context);
    return (minimum === undefined || val > minimum) && (maximum === undefined || val < maximum);
  }

  public withIsAvailable(isAvailable: (context: Context) => boolean): this {
    this.isAvailable = isAvailable;
    return this;
  }

  public withDefaultValue(getDefaultValue: (context?: Context) => DefaultValue<Date> | null): this {
    this.getDefaultValue = getDefaultValue;
    return this;
  }

  public withRequired(isRequired?: boolean): this { // true is default setting for this function
    this.required = isRequired !== false;
    return this;
  }

  public withFieldUsage(fieldUsage: FieldUsage): this {
    this.fieldUsage = fieldUsage;
    return this;
  }

  // TODO make parameter mandatory
  public vValidateRule(context?: Context, isRenFrance?: boolean): string {
    const format = isRenFrance ? 'DD/MM/YYYY' : this.format;
    const fnsFormat = format.replace(/Y/g, 'y').replace(/D/g, 'd').replace(/M/g, 'm');
    const dateFormat = `date_format:${fnsFormat}`;
    const constraints = [];
    if (this.required) {
      constraints.push('required');
    }
    constraints.push(dateFormat);
    const min = this.minimum(context);
    const max = this.maximum(context);
    if (min && !max) {
      constraints.push(`after:${_t.formatDate(min, format)}`);
    }
    if (max && !min) {
      constraints.push(`before:${_t.formatDate(max, format)}`);
    }
    if (max && min) {
      constraints.push(`date_between:${_t.formatDate(min, format)},${_t.formatDate(max, format)}`);
    }
    return constraints.join('|');
  }

  public minimum(context?: Context): Date | undefined {
    return this.readValue(context, this.min);
  }

  public maximum(context?: Context): Date | undefined {
    return this.readValue(context, this.max);
  }

  private readValue(context: Context | undefined, value: Date | string | undefined): Date | undefined {
    if (value instanceof Date) {
      return value;
    }
    if (typeof value === 'string') {
      const [, ...path] = value.split('.');
      const valueFromContext = getValueFromPath(context, path);
      if (valueFromContext instanceof Date || typeof valueFromContext === 'string') {
        return new Date(valueFromContext);
      }
    }
  }
}
